/**
 * Connection state machine — zustand slice.
 *
 * Tracks the WebSocket lifecycle per CLIENT_DESIGN §3 + WIRE_PROTOCOL §4:
 *
 *   disconnected → handshaking → (snapshotting | replaying) → active
 *                                                              ↓
 *                                                          reconnecting
 *                                                              ↓
 *                                                          handshaking …
 *
 * The store is consumed by:
 *   - `WireClient` (sets the state; emits state-transition side effects)
 *   - React shell components (show connection-health indicator, reconnect
 *     overlay, error toasts)
 *   - Rate-limit throttle (stops sending intents when `stage !== 'active'`)
 */

import { create } from 'zustand';

import type { ErrorCode, ReconnectMode } from './envelopes';

export type ConnectionStage =
  | 'disconnected'
  | 'handshaking'
  | 'snapshotting'
  | 'replaying'
  | 'active'
  | 'reconnecting';

export interface ConnectionState {
  stage: ConnectionStage;
  /** Reconnect-backoff attempt counter (0 when the current connection is
   *  the initial one; resets to 0 on successful handshake). */
  attempt: number;
  /** Round-trip time in ms from the most recent ping/pong exchange. */
  latency_ms: number | null;
  /** Latest server_time_ms - client_time_ms offset, for clock alignment. */
  server_clock_skew_ms: number | null;
  /** If the last transition was into an error state, the cause. */
  last_error: { code: ErrorCode; reason: string } | null;
  /** Last reconnect_mode the server sent (informational — the stage has
   *  already branched on this by the time it lands here). */
  last_reconnect_mode: ReconnectMode | null;

  // ─── Actions ─────────────────────────────────────────────────────────
  setStage: (next: ConnectionStage) => void;
  setLatency: (rtt_ms: number) => void;
  setClockSkew: (skew_ms: number) => void;
  setError: (err: { code: ErrorCode; reason: string } | null) => void;
  setReconnectMode: (mode: ReconnectMode) => void;
  incrementAttempt: () => void;
  resetAttempt: () => void;
  reset: () => void;
}

const initialState = {
  stage: 'disconnected' as ConnectionStage,
  attempt: 0,
  latency_ms: null,
  server_clock_skew_ms: null,
  last_error: null,
  last_reconnect_mode: null,
};

export const useConnection = create<ConnectionState>((set) => ({
  ...initialState,

  setStage: (next) => set(() => ({ stage: next })),
  setLatency: (rtt_ms) => set(() => ({ latency_ms: rtt_ms })),
  setClockSkew: (skew_ms) => set(() => ({ server_clock_skew_ms: skew_ms })),
  setError: (err) => set(() => ({ last_error: err })),
  setReconnectMode: (mode) => set(() => ({ last_reconnect_mode: mode })),
  incrementAttempt: () => set((s) => ({ attempt: s.attempt + 1 })),
  resetAttempt: () => set(() => ({ attempt: 0 })),
  reset: () => set(() => ({ ...initialState })),
}));
